import { Component, OnInit, Optional, Inject, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { InternationalAddressService } from 'src/app/services/international-address.service';
import { LocalizationService } from 'src/app/services/localization.service';
import { timeZones, getTimeZoneDisplay, getTimezonesByCountry } from 'src/app/constants/time-zones';
import { Subject, takeUntil } from 'rxjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-form-studio-information',
  templateUrl: './form-studio-information.component.html',
  styleUrls: ['./form-studio-information.component.css']
})
export class FormStudioInformationComponent implements OnInit, OnDestroy {
  studioInformationForm: FormGroup;
  actionType: 'create' | 'edit' = 'create';
  showProgressSpinner = false;
  successMessage: string;
  errorMessage: string;

  timeZones = timeZones;
  filteredTimeZones = timeZones; // Start with all timezones
  getTimeZoneDisplay = getTimeZoneDisplay;

  // Current administrative area label based on selected country
  administrativeAreaLabel = 'State';

  // Current postal code label based on selected country
  postalCodeLabel = 'ZIP Code';

  // Phone country for ngx-intl-tel-input (detected from E.164 number)
  phoneCountry: string = 'US';

  // Subscription cleanup
  private destroy$ = new Subject<void>();

  // Custom validator for phone number
  private phoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    // If empty, return required error
    if (!value) {
      return { required: true };
    }

    // If it's an object (from ngx-intl-tel-input), check for e164Number
    if (typeof value === 'object') {
      if (value.e164Number && value.e164Number.length > 0) {
        return null; // Valid
      }
      return { invalidPhone: true };
    }

    // If it's a string and not empty, it's valid
    if (typeof value === 'string' && value.trim().length > 0) {
      return null;
    }

    return { invalidPhone: true };
  }

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService,
    public _fieldsService: FieldsService,
    public internationalAddressService: InternationalAddressService,
    private localizationService: LocalizationService,
    @Optional() public dialogRef: MatDialogRef<FormStudioInformationComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {}

  ngOnInit(): void {
    this.studioInformationForm = this.fb.group({
      studioDisplayName: ['', Validators.required],
      studioEmail: ['', [Validators.required, Validators.email]],
      studioPhoneNumber: ['', this.phoneValidator.bind(this)],
      country: ['', Validators.required],
      isBonded: [false],
      currencyCode: ['USD', Validators.required],
      studioWebsite: this.fb.group({
        uri: ['', Validators.required],
        title: ['', Validators.required]
      }),
      studioAddress: this.fb.group({
        givenName: ['', Validators.required],
        familyName: ['', Validators.required],
        company: [''],
        streetAddress: ['', Validators.required],
        city: ['', Validators.required],
        state: [''],
        zipCode: ['', Validators.required]
      }),
      vatNumber: [''],
      studioTimeZone: ['', Validators.required]
    });

    // Set up country change listener with proper cleanup
    this.studioInformationForm.get('country').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(country => {
        this.onCountryChange(country);
      });

    // Phone number formatting is now handled by the appPhoneNumberInput directive

    this.loadStudioInformation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Handle country change to update administrative area label and validation
   */
  onCountryChange(countryCode: string) {
    const stateControl = this.studioInformationForm.get('studioAddress.state');

    // Update administrative area label based on country
    this.administrativeAreaLabel = this.internationalAddressService.getAdministrativeAreaLabel(countryCode);

    // Update postal code label based on country
    this.postalCodeLabel = this.internationalAddressService.getPostalCodeLabel(countryCode);

    // Filter timezones based on selected country
    this.filteredTimeZones = getTimezonesByCountry(countryCode);

    // Auto-select currency based on country
    const currency = this.localizationService.getCurrencyForCountry(countryCode);
    if (currency) {
      this.studioInformationForm.get('currencyCode').setValue(currency, { emitEvent: false });
    }

    // If country has predefined administrative areas, make the field required
    if (this.internationalAddressService.hasAdministrativeAreas(countryCode)) {
      stateControl.setValidators([Validators.required]);
    } else {
      // For countries without predefined areas, make it optional
      stateControl.clearValidators();
    }

    stateControl.updateValueAndValidity();
  }

  loadStudioInformation() {
    const configFieldName = 'field_studio_information_ref';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          const timezoneValue = response?.['field_studio_timezone']?.[0]?.value;
          const countryCode = response?.['field_studio_address']?.[0]?.country_code;
          let phoneNumberRaw = response?.['field_studio_phone_number']?.[0]?.value;

          // Parse E.164 phone number to extract country and national number
          let phoneNumber = '';
          if (phoneNumberRaw) {
            try {
              // Parse the E.164 number (with or without + prefix)
              const parsed = parsePhoneNumberFromString(phoneNumberRaw);
              if (parsed) {
                // Set phone country for ngx-intl-tel-input
                this.phoneCountry = parsed.country || 'US';
                // Extract national number only (without country code)
                phoneNumber = parsed.nationalNumber;
              } else {
                // Fallback if parsing fails
                phoneNumber = phoneNumberRaw;
                this.phoneCountry = 'US';
              }
            } catch (e) {
              LoggerService.warn('Failed to parse phone number:', phoneNumberRaw, e);
              phoneNumber = phoneNumberRaw;
              this.phoneCountry = 'US';
            }
          }

          const formData = {
            studioDisplayName: response?.['field_studio_display_name']?.[0]?.value,
            studioEmail: response?.['field_studio_email']?.[0]?.value,
            studioPhoneNumber: phoneNumber,
            country: countryCode,
            isBonded: response?.['field_studio_is_bonded']?.[0]?.value === '1' || response?.['field_studio_is_bonded']?.[0]?.value === true,
            currencyCode: response?.['field_currency_code']?.[0]?.value || 'USD',
            studioWebsite: {
              uri: response?.['field_studio_website']?.[0]?.uri,
              title: response?.['field_studio_website']?.[0]?.title
            },
            studioAddress: {
              givenName: response?.['field_studio_address']?.[0]?.given_name,
              familyName: response?.['field_studio_address']?.[0]?.family_name,
              company: response?.['field_studio_address']?.[0]?.organization,
              streetAddress: response?.['field_studio_address']?.[0]?.address_line1,
              city: response?.['field_studio_address']?.[0]?.locality,
              state: response?.['field_studio_address']?.[0]?.administrative_area,
              zipCode: response?.['field_studio_address']?.[0]?.postal_code
            },
            vatNumber: response?.['field_studio_vat_number']?.[0]?.value,
            studioTimeZone: timezoneValue
          };

          // First trigger country change to set up filtered timezones and other country-specific settings
          if (countryCode) {
            this.onCountryChange(countryCode);

            // If the saved timezone is not in the filtered list for this country,
            // we need to add it to ensure it's available for selection
            if (timezoneValue) {
              const timezoneExists = this.filteredTimeZones.some(tz => tz.value === timezoneValue);
              if (!timezoneExists) {
                const fullTimezone = this.timeZones.find(tz => tz.value === timezoneValue);
                if (fullTimezone) {
                  // Add the timezone to the filtered list if it exists in the full list
                  this.filteredTimeZones = [...this.filteredTimeZones, fullTimezone];
                }
              }
            }
          }

          // Now patch the form values
          this.studioInformationForm.patchValue(formData);
        },
        (error) => {
          LoggerService.error('Error loading studio information:', error);
          this.errorMessage = 'Error loading studio information.';
        }
      );
  }

  saveStudioInformation() {
    const configFieldName = 'field_studio_information_ref';
    const formValues = this.studioInformationForm.value;

    // ngx-intl-tel-input returns an object with e164Number property
    // Store in E.164 format WITH + prefix (international standard)
    let cleanedPhoneNumber = '';
    if (formValues.studioPhoneNumber) {
      if (typeof formValues.studioPhoneNumber === 'object' && formValues.studioPhoneNumber.e164Number) {
        // Keep E.164 format with + prefix
        cleanedPhoneNumber = formValues.studioPhoneNumber.e164Number;
      } else if (typeof formValues.studioPhoneNumber === 'string') {
        // Fallback if it's a string - ensure it has + prefix
        cleanedPhoneNumber = formValues.studioPhoneNumber.startsWith('+')
          ? formValues.studioPhoneNumber
          : '+' + formValues.studioPhoneNumber;
      }
    }

    const postData = {
      config_field_name: configFieldName,
      config_data: {
        field_studio_display_name: [{value: formValues.studioDisplayName}],
        field_studio_email: [{value: formValues.studioEmail}],
        field_studio_phone_number: [{value: cleanedPhoneNumber}],
        field_studio_is_bonded: [{value: formValues.isBonded ? '1' : '0'}],
        field_currency_code: [{value: formValues.currencyCode}],
        field_studio_website: [{
          uri: formValues.studioWebsite.uri,
          title: formValues.studioWebsite.title
        }],
        field_studio_address: [{
          country_code: formValues.country,
          given_name: formValues.studioAddress.givenName,
          family_name: formValues.studioAddress.familyName,
          organization: formValues.studioAddress.company,
          address_line1: formValues.studioAddress.streetAddress,
          locality: formValues.studioAddress.city,
          administrative_area: formValues.studioAddress.state,
          postal_code: formValues.studioAddress.zipCode
        }],
        field_studio_vat_number: [{value: formValues.vatNumber || ''}],
        field_studio_timezone: [{value: formValues.studioTimeZone}]
      }
    };

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', postData)
      .subscribe(
        (response) => {
          LoggerService.log('Studio information saved successfully:', response);
          this.successMessage = 'Studio information saved successfully.';
          this.showProgressSpinner = false;

          // Close dialog immediately after successful save if in dialog mode
          if (this.dialogRef) {
            this.dialogRef.close(true);
          }
        },
        (error) => {
          LoggerService.error('Error saving studio information:', error);
          this.errorMessage = 'Error saving studio information.';
          this.showProgressSpinner = false;
        }
      );
  }

  onSubmit() {
    this.saveStudioInformation();
  }

  onUpdate() {
    this.saveStudioInformation();
  }

  /**
   * Show all available timezones regardless of selected country
   */
  showAllTimezones() {
    this.filteredTimeZones = this.timeZones;

    // Need to set timeout to allow the mat-select to update before setting a value
    setTimeout(() => {
      // Reset the timezone value since 'show_all' is not a valid timezone
      const currentTimezone = this.studioInformationForm.get('studioTimeZone').value;
      if (currentTimezone === 'show_all') {
        this.studioInformationForm.get('studioTimeZone').setValue('');
      }
    });
  }
}
